import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Logo from "../../../assests/logo.png";
import { getBranchDisplayInfo, getQueues } from "../../../api/api";
import { getStorageUrl } from "../../../config/config";
import useQueueUpdates from "../../../hooks/useQueueUpdates";

const Display = () => {
    const [searchParams] = useSearchParams();
    const branchId = searchParams.get("branch_id");
    const [logoUrl, setLogoUrl] = useState(null);
    const [currentQueueNumber, setCurrentQueueNumber] = useState("00");

    useEffect(() => {
        if (!branchId) {
            setLogoUrl(Logo);
            return;
        }
        getBranchDisplayInfo(branchId)
            .then((data) => {
                if (data?.logo) {
                    setLogoUrl(getStorageUrl(`/storage/logos/${data.logo}`));
                } else {
                    setLogoUrl(Logo);
                }
            })
            .catch(() => setLogoUrl(Logo));
    }, [branchId]);

    // Fetch latest queue number for the branch.
    // The display must always show the LATEST Assist-clicked queue number, even
    // when multiple queues are SERVING simultaneously or when the latest
    // assist-clicked queue has since been marked Done. The backend already returns
    // exactly that in `last_queue_number` — it orders SERVING+ASSISTED queues by
    // QueueSvcTime desc (the actual assist-click time, which is no longer
    // overwritten on Done/auto-expire), so we just trust it directly.
    const fetchCurrentQueue = useCallback(async () => {
        if (!branchId) return;

        try {
            const data = await getQueues(branchId);
            const lastQueueNumber = Array.isArray(data) ? null : data?.last_queue_number;

            if (lastQueueNumber != null) {
                setCurrentQueueNumber(String(lastQueueNumber).padStart(2, "0"));
            }
        } catch (err) {
            console.error("Error fetching current queue:", err);
        }
    }, [branchId]);

    useEffect(() => {
        fetchCurrentQueue();
    }, [fetchCurrentQueue]);

    // Real-time updates via Pusher (Assist or Direct Assist). The hook falls
    // back to polling only while the socket is down.
    useQueueUpdates({
        branchId,
        onRefresh: fetchCurrentQueue,
        onQueueEvent: (queueData, action) => {
            const isServing =
                queueData.QueueStatus === "SERVING" ||
                queueData.status === "SERVING" ||
                queueData.QueueStatus === "serving" ||
                queueData.status === "serving";
            const isDirectAssist = action === "direct_assist";

            if (isServing || isDirectAssist) {
                setCurrentQueueNumber(
                    String(queueData.QueueNr || queueData.number || "00").padStart(2, "0")
                );
            }
        },
    });

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                minHeight: "100vh",
                padding: "24px 16px",
                boxSizing: "border-box",
            }}
        >
            {logoUrl && (
                <img
                    src={logoUrl}
                    alt="Logo"
                    style={{
                        width: "min(28%, 300px)",
                        maxWidth: "90vw",
                        maxHeight: "25vh",
                        height: "auto",
                        objectFit: "contain",
                        marginBottom: "clamp(16px, 4vh, 50px)",
                    }}
                    onError={(e) => {
                        e.target.src = Logo;
                    }}
                />
            )}
            <div
                style={{
                    fontSize: "clamp(60px, 15vw, 180px)",
                    lineHeight: 1,
                    textAlign: "center",
                }}
            >
                <h1 style={{ margin: 0 }}>{currentQueueNumber}</h1>
            </div>
        </div>
    );
};

export default Display;
